import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { IExportState, ReferenceLine, StateType } from '@/version-types';
import { getDefaultReferenceLine } from '../utils';

/** 状态 */
interface IState {
    /** 是否显示参考线 */
    showReferenceLines: boolean;
    /** 是否锁定参考线 */
    lockReferenceLines: boolean;
    /** 参考线列表 */
    referenceLines: ReferenceLine[];
    /** 记录拖拽的参考线 id, 需要高亮显示 */
    draggingLineId: string;
    /** 记录拖拽的参考线 */
    draggingLine: ReferenceLine | null;
    /** 记录 tooltip 的位置 */
    tooltipPosition: {
        x: number;
        y: number;
    };
    /** 记录容器的位置 */
    containerRect: DOMRect | null;
    /** 记录标尺是否显示 */
    rulerBarSize: number;
}

/** 动作 */
interface IAction {
    /** 设置参考线 */
    setReferenceLines: (referenceLines: ReferenceLine[]) => void;
    /** 设置启用、禁用参考线 */
    setShowReferenceLines: (show: boolean) => void;
    /** 锁定、解锁参考线 */
    setLockReferenceLines: (lock: boolean) => void;
    /** 添加参考线 */
    addReferenceLine: (line: Partial<ReferenceLine>) => void;
    /** 删除参考线 */
    deleteReferenceLine: (id: string) => void;
    /** 更新参考线 */
    updateReferenceLine: (line: Partial<ReferenceLine>) => void;
    /** 设置拖拽的参考线 id */
    setDraggingLineId: (id: string) => void;
    /** 设置拖拽的参考线 */
    setDraggingLine: (line: ReferenceLine | null) => void;
    /** 设置 tooltip 的位置 */
    setTooltipPosition: (position: { x: number; y: number }) => void;
    /** 设置容器的位置 */
    setContainerRect: (rect: DOMRect | null) => void;
    /** 设置标尺是否显示 */
    setRulerBarSize: (size: number) => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState() {
    return {
        showReferenceLines: false,
        lockReferenceLines: false,
        highlightReferenceLineIds: [],
        draggingLineId: '',
        tooltipPosition: {
            x: 0,
            y: 0,
        },
        draggingLine: null,
        referenceLines: [],
        containerRect: null,
        rulerBarSize: 0,
    };
}

/** 创建存储 */
const createState = () => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),

        setReferenceLines(lines) {
            set({ referenceLines: lines });
        },
        addReferenceLine(line) {
            const l = get().referenceLines.find((item) => item.id === line.id);
            if (l) return;
            const newLine = Object.assign({}, getDefaultReferenceLine(), line);

            set({
                referenceLines: [...get().referenceLines, newLine],
            });
        },
        deleteReferenceLine(id) {
            const l = get().referenceLines.find((item) => item.id === id);
            if (!l) return;
            set({
                referenceLines: get().referenceLines.filter((line) => line.id !== id),
            });
        },
        updateReferenceLine(line) {
            const l = get().referenceLines.find((item) => item.id === line.id);
            if (!l) return;
            set({
                referenceLines: get().referenceLines.map((l) => {
                    if (l.id === line.id) {
                        return Object.assign({}, l, line);
                    }
                    return l;
                }),
            });
        },
        setShowReferenceLines(show) {
            set({ showReferenceLines: show });
        },
        setLockReferenceLines(lock) {
            set({ lockReferenceLines: lock });
        },
        setDraggingLineId(id) {
            set({ draggingLineId: id });
        },
        setTooltipPosition(position) {
            set({ tooltipPosition: position });
        },
        setDraggingLine(line) {
            set({ draggingLine: line });
        },
        setContainerRect(rect) {
            set({ containerRect: rect });
        },
        setRulerBarSize(size) {
            set({ rulerBarSize: size });
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'reference-lines-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useReferenceLinesState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getReferenceLinesState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
